import React, { useEffect, useState } from "react";
import { Download, Smartphone, X, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

/**
 * InstallAppButton — pulsante "Installa app"
 * Comportamento:
 *  - Android/Chrome/Edge: intercetta `beforeinstallprompt` e mostra il prompt nativo.
 *  - iOS Safari (non supporta il prompt): apre una guida "Aggiungi a Home".
 *  - Se l'app è già installata (display-mode: standalone), non mostra nulla.
 *
 * Props:
 *  - variant: "cta" (grande gradient) | "ghost" (compatto trasparente per hero)
 */
export default function InstallAppButton({ variant = "cta", className = "" }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [iosGuideOpen, setIosGuideOpen] = useState(false);

  useEffect(() => {
    const ua = window.navigator.userAgent.toLowerCase();
    const iOSDevice = /iphone|ipad|ipod/.test(ua) && !window.MSStream;
    setIsIOS(iOSDevice);

    const standalone = window.matchMedia("(display-mode: standalone)").matches ||
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Se già installata → non mostro nulla
  if (isStandalone || installed) return null;
  // Se non è iOS e non ho ancora il prompt (browser non supporta) → non mostro
  if (!isIOS && !deferred) return null;

  const handleClick = async () => {
    if (isIOS) {
      setIosGuideOpen(true);
      return;
    }
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setDeferred(null);
  };

  const label = "Installa app sul telefono";

  const btn = variant === "ghost" ? (
    <button onClick={handleClick} data-testid="install-app-btn"
            className={`inline-flex items-center gap-2 text-[12px] font-semibold
                        text-emerald-300 hover:text-emerald-200 transition-colors
                        underline-offset-4 hover:underline ${className}`}>
      <Smartphone className="h-3.5 w-3.5" /> {label}
    </button>
  ) : variant === "outline" ? (
    <Button onClick={handleClick} data-testid="install-app-btn"
            variant="outline"
            className={`h-11 rounded-xl border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-[#1E3A8A] hover:border-[#1E3A8A]/30 ${className}`}>
      <Download className="h-4 w-4 mr-2 text-[#10B981]" /> Installa app
    </Button>
  ) : (
    <Button onClick={handleClick} data-testid="install-app-btn"
            className={`h-11 rounded-xl bg-gradient-to-r from-[#1E3A8A] via-[#2563EB] to-[#10B981]
                        hover:from-[#1E40AF] hover:via-[#3B82F6] hover:to-[#059669]
                        text-white font-semibold shadow-md shadow-emerald-500/10 ${className}`}>
      <Download className="h-4 w-4 mr-2" /> Installa Synappsi
    </Button>
  );

  return (
    <>
      {btn}
      <Dialog open={iosGuideOpen} onOpenChange={setIosGuideOpen}>
        <DialogContent className="max-w-md" data-testid="ios-install-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-[#1E3A8A]" />
              Installa Synappsi su iPhone / iPad
            </DialogTitle>
            <DialogDescription>
              Su iOS Safari non c'è un pulsante automatico. Ti basta seguire 3 passaggi:
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm text-slate-700 mt-2">
            <li className="flex gap-3">
              <span className="flex-shrink-0 h-6 w-6 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#10B981] text-white font-bold text-xs flex items-center justify-center">1</span>
              <span>Tocca il tasto <strong>Condividi</strong> <Share className="inline h-4 w-4 text-[#1E3A8A] align-middle" /> in fondo (o in alto) al browser.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 h-6 w-6 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#10B981] text-white font-bold text-xs flex items-center justify-center">2</span>
              <span>Scorri e scegli <strong>"Aggiungi alla schermata Home"</strong>.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 h-6 w-6 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#10B981] text-white font-bold text-xs flex items-center justify-center">3</span>
              <span>Conferma con <strong>"Aggiungi"</strong>. L'icona Synappsi apparirà sulla Home come una vera app.</span>
            </li>
          </ol>
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
            <Smartphone className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
            <span>Una volta installata potrai aprirla come un'app nativa (icona sinapsi), ricevere notifiche push per valori d'allarme e usarla a schermo intero.</span>
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={() => setIosGuideOpen(false)} variant="outline" data-testid="ios-install-close">
              <X className="h-4 w-4 mr-1" /> Ho capito
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
